(function () {
  const EMAIL_STORAGE_KEY = 'blovo-contact-ticket-email';
  const MAX_IMAGES_DEFAULT = 9;
  const MAX_IMAGE_BYTES_DEFAULT = 10 * 1024 * 1024;
  const MAX_VIDEO_BYTES_DEFAULT = 20 * 1024 * 1024;

  function formatMb(bytes) {
    return Math.round((bytes / (1024 * 1024)) * 10) / 10;
  }

  function getConfig(sectionId) {
    const el = document.getElementById('ContactTicketConfig-' + sectionId);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function validateEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function setFieldError(field, message) {
    if (!field) return;
    const row = field.closest('.ticket-form__row');
    const errorEl = row ? row.querySelector('.ticket-form__error') : null;
    if (errorEl) errorEl.textContent = message || '';
    if (field.setAttribute) field.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  function debounce(fn, wait) {
    let timer;
    return function debounced() {
      const args = arguments;
      const ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  async function proxyFetch(config, path, options) {
    const base = (config.apiProxyBase || '/apps/blovo-bridge').replace(/\/$/, '');
    const response = await fetch(base + path, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options && options.headers ? options.headers : {})
      }
    });
    const text = await response.text();
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch (error) {
      payload = { message: text };
    }
    if (!response.ok) {
      let message =
        (payload && (payload.message || (payload.error && (payload.error.message || payload.error)))) ||
        'Request failed (' + response.status + ')';
      if (typeof message === 'object' && message.message) message = message.message;
      if (response.status === 404) {
        message =
          'Support API is not available on this store yet. If you are logged in, we will use your account orders instead.';
      }
      throw new Error(message);
    }
    return payload;
  }

  function resolveRules(config, categoryHandle, subcategory) {
    const rulesRoot = config.fieldRules || {};
    const defaults = Object.assign(
      { requireOrder: 'optional', requireProduct: 'no', requireImage: 'no', requireVideo: 'no' },
      rulesRoot.default || {}
    );
    const categoryRules = (rulesRoot.categories && rulesRoot.categories[categoryHandle]) || {};
    const subRules =
      (subcategory && categoryRules[subcategory]) ||
      categoryRules.default ||
      {};
    return Object.assign({}, defaults, subRules);
  }

  function isRequired(level) {
    return level === 'yes';
  }

  function isOptional(level) {
    return level === 'optional';
  }

  function isHidden(level) {
    return level === 'no';
  }

  function formatQuestionLabel(categoryMap, categoryHandle, subcategory) {
    const label = (categoryMap || {})[categoryHandle] || categoryHandle;
    return subcategory ? label + ' / ' + subcategory : label;
  }

  function readInitialParams(categoryMap) {
    const params = new URLSearchParams(window.location.search);
    let category =
      params.get('category') ||
      params.get('categories') ||
      '';
    let subcategory =
      params.get('subcategory') ||
      params.get('category2') ||
      '';

    if (!category && params.get('category1')) {
      const label = params.get('category1');
      const entry = Object.entries(categoryMap || {}).find(([, name]) => name === label);
      category = entry ? entry[0] : label;
    }
    if (!subcategory && params.get('category3')) {
      subcategory = params.get('category3');
    }

    const parent = (params.get('parent') || '').trim();

    return { category, subcategory, parent };
  }

  function initSection(root) {
    const sectionId = root.dataset.sectionId;
    const config = getConfig(sectionId);
    if (!config) return;

    const form = root.querySelector('[data-ticket-form]');
    const spinEl = root.querySelector('[data-ticket-spin]');
    const successEl = root.querySelector('[data-ticket-success]');
    const errorBanner = root.querySelector('[data-ticket-error]');
    const submitButton = root.querySelector('[data-ticket-submit]');

    const questionTypeTrigger = root.querySelector('[data-ticket-question-trigger]');
    const questionTypeLabel = root.querySelector('[data-ticket-question-label]');
    const questionPanel = root.querySelector('[data-ticket-question-panel]');
    const questionTopicsCol = root.querySelector('[data-ticket-question-topics]');
    const questionSubtopicsCol = root.querySelector('[data-ticket-question-subtopics]');
    const categoryInput = root.querySelector('[data-ticket-category]');
    const subcategoryInput = root.querySelector('[data-ticket-subcategory]');
    const emailInput = root.querySelector('[data-ticket-email]');
    const nameInput = root.querySelector('[data-ticket-name]');
    const messageInput = root.querySelector('[data-ticket-message]');

    const orderRow = root.querySelector('[data-ticket-order-row]');
    const orderSelect = root.querySelector('[data-ticket-order]');
    const orderLabel = root.querySelector('[data-ticket-order-label]');
    const orderHint = root.querySelector('[data-ticket-order-hint]');

    const productRow = root.querySelector('[data-ticket-product-row]');
    const productLabel = root.querySelector('[data-ticket-product-label]');
    const pickProductBtn = root.querySelector('[data-ticket-pick-product]');
    const productPanel = root.querySelector('[data-ticket-product-panel]');

    const imageRow = root.querySelector('[data-ticket-image-row]');
    const imageLabel = root.querySelector('[data-ticket-image-label]');
    const imageInput = root.querySelector('[data-ticket-images]');
    const imagePreview = root.querySelector('[data-ticket-image-preview]');
    const imageCount = root.querySelector('[data-ticket-image-count]');

    const videoInput = root.querySelector('[data-ticket-video]');
    const videoPreview = root.querySelector('[data-ticket-video-preview]');
    const videoCount = root.querySelector('[data-ticket-video-count]');

    const categoryMap = config.categoryMap || {};
    const initial = readInitialParams(categoryMap);

    if (initial.parent && /^\/(?!\/)/.test(initial.parent)) {
      const backLink = root.querySelector('[data-ticket-back]');
      if (backLink) backLink.setAttribute('href', initial.parent);
    }

    const maxImages = config.maxImages || MAX_IMAGES_DEFAULT;
    const maxImageBytes = config.maxImageBytes || MAX_IMAGE_BYTES_DEFAULT;
    const maxVideoBytes = config.maxVideoBytes || MAX_VIDEO_BYTES_DEFAULT;

    let rules = resolveRules(config, initial.category, initial.subcategory);
    let ordersCache = Array.isArray(config.preloadedOrders) ? config.preloadedOrders.slice() : [];
    let ordersCacheEmail = (config.customerEmail || '').trim().toLowerCase();
    let selectedOrder = null;
    let selectedProducts = [];
    const uploadedImages = [];
    let uploadedVideo = null;
    let questionTree = [];
    let questionActiveCategory = null;

    function setSpinning(active) {
      if (!spinEl) return;
      spinEl.classList.toggle('is-loading', active);
      if (submitButton) submitButton.disabled = active;
    }

    function restoreEmail() {
      const stored = localStorage.getItem(EMAIL_STORAGE_KEY);
      if (stored && !emailInput.value) {
        emailInput.value = stored;
      }
    }

    function buildQuestionTypeTree() {
      const categoryRules = (config.fieldRules && config.fieldRules.categories) || {};
      const entries = Object.entries(categoryMap);
      questionTree = entries.map(function (entry) {
        const handle = entry[0];
        const label = entry[1] || handle;
        const rulesForCategory = categoryRules[handle] || {};
        const subtopics = Object.keys(rulesForCategory).filter(function (key) {
          return key && key !== 'default';
        });
        return {
          category: handle,
          label,
          subtopics
        };
      });
    }

    function renderQuestionCascade(activeCategory) {
      if (!questionTopicsCol) return;
      questionActiveCategory = activeCategory || null;
      questionTopicsCol.innerHTML = '';

      questionTree.forEach(function (topic) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ticket-question-panel__item';
        if (topic.subtopics.length) {
          button.classList.add('ticket-question-panel__item--parent');
        }
        if (topic.category === questionActiveCategory) {
          button.classList.add('ticket-question-panel__item--active');
        }
        button.textContent = topic.label;
        button.addEventListener('mouseenter', function () {
          if (topic.subtopics.length) {
            setActiveTopic(topic.category);
          }
        });
        button.addEventListener('click', function () {
          if (!topic.subtopics.length) {
            selectQuestionType(topic.category, '');
            closeQuestionDrawer();
            return;
          }
          setActiveTopic(topic.category);
        });
        questionTopicsCol.appendChild(button);
      });

      renderQuestionSubtopics(questionActiveCategory);
    }

    function setActiveTopic(category) {
      questionActiveCategory = category;
      if (questionTopicsCol) {
        Array.prototype.forEach.call(questionTopicsCol.children, function (child, index) {
          const topic = questionTree[index];
          child.classList.toggle(
            'ticket-question-panel__item--active',
            !!topic && topic.category === category
          );
        });
      }
      renderQuestionSubtopics(category);
    }

    function renderQuestionSubtopics(category) {
      if (!questionSubtopicsCol) return;
      questionSubtopicsCol.innerHTML = '';
      const topic = questionTree.find(function (item) {
        return item.category === category;
      });
      if (!topic || !topic.subtopics.length) return;

      topic.subtopics.forEach(function (subtopic) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ticket-question-panel__item';
        if (categoryInput.value === topic.category && subcategoryInput.value === subtopic) {
          button.classList.add('ticket-question-panel__item--active');
        }
        button.textContent = subtopic;
        button.addEventListener('click', function () {
          selectQuestionType(topic.category, subtopic);
          closeQuestionDrawer();
        });
        questionSubtopicsCol.appendChild(button);
      });
    }

    function openQuestionDrawer() {
      if (!questionPanel) return;
      questionPanel.hidden = false;
      if (questionTypeTrigger) questionTypeTrigger.setAttribute('aria-expanded', 'true');

      let active = null;
      if (categoryInput.value) {
        const current = questionTree.find(function (item) {
          return item.category === categoryInput.value;
        });
        if (current && current.subtopics.length) {
          active = current.category;
        }
      }
      if (!active) {
        const firstParent = questionTree.find(function (item) {
          return item.subtopics.length;
        });
        active = firstParent ? firstParent.category : null;
      }
      renderQuestionCascade(active);
    }

    function closeQuestionDrawer() {
      if (!questionPanel) return;
      questionPanel.hidden = true;
      if (questionTypeTrigger) questionTypeTrigger.setAttribute('aria-expanded', 'false');
    }

    function selectQuestionType(category, subcategory) {
      categoryInput.value = category || '';
      subcategoryInput.value = subcategory || '';
      applyQuestionType();
      applyFieldRules();
      setFieldError(questionTypeTrigger, '');
    }

    function applyQuestionType() {
      const category = categoryInput.value;
      const subcategory = subcategoryInput.value;
      if (questionTypeLabel) {
        const label = categoryMap[category] || category;
        questionTypeLabel.textContent = category
          ? (subcategory ? label + ' / ' + subcategory : label)
          : 'Please select question type';
      }
    }

    function resetOrderAndProducts() {
      selectedOrder = null;
      selectedProducts = [];
      ordersCache = [];
      ordersCacheEmail = '';
      if (orderSelect) {
        orderSelect.innerHTML = '<option value="">Please select order</option>';
        orderSelect.value = '';
      }
      if (productPanel) {
        productPanel.hidden = true;
        productPanel.innerHTML = '';
      }
      if (pickProductBtn) {
        const placeholder = pickProductBtn.querySelector('.ticket-pick-product__placeholder');
        if (placeholder) placeholder.hidden = false;
      }
    }

    function applyFieldRules() {
      const category = categoryInput.value;
      rules = resolveRules(config, category, subcategoryInput.value);

      const showOrderOnly =
        !isHidden(rules.requireOrder) && isHidden(rules.requireProduct);
      const showProductPicker = !isHidden(rules.requireProduct);

      if (orderRow) {
        orderRow.hidden = !showOrderOnly;
        orderSelect.required = isRequired(rules.requireOrder);
        if (orderLabel) {
          orderLabel.innerHTML = isRequired(rules.requireOrder)
            ? 'Order Number <span>*</span>'
            : 'Order Number';
        }
      }

      if (productRow) {
        productRow.hidden = !showProductPicker;
        if (productLabel) {
          productLabel.textContent = isRequired(rules.requireProduct) ? '* Product' : 'Product';
        }
      }

      if (imageLabel) {
        imageLabel.textContent = isRequired(rules.requireImage) ? '* Image:' : 'Image:';
      }

      if (!showOrderOnly) {
        orderSelect.required = false;
      }
    }

    function updateUploadCounters() {
      if (imageCount) imageCount.textContent = uploadedImages.length + '/' + maxImages;
      if (videoCount) videoCount.textContent = (uploadedVideo ? 1 : 0) + '/1';
    }

    function renderProductPanel(order, products) {
      if (!productPanel) return;
      productPanel.hidden = false;
      productPanel.innerHTML =
        '<div class="ticket-order-card">' +
        '<div class="ticket-order-card__head">' +
        '<span>' +
        (order.name || order.order_number || 'Order') +
        '</span>' +
        '<span class="ticket-order-card__status">' +
        (order.status || '') +
        '</span>' +
        '</div>' +
        '<div class="ticket-order-card__products"></div>' +
        '</div>';

      const list = productPanel.querySelector('.ticket-order-card__products');
      (products || []).forEach(function (item) {
        const id = String(item.id || item.title);
        const label = document.createElement('label');
        label.className = 'ticket-product-line';
        label.innerHTML =
          '<input type="checkbox" value="' +
          id +
          '" data-title="' +
          (item.title || 'Item').replace(/"/g, '&quot;') +
          '" data-quantity="' +
          (item.quantity || 1) +
          '" data-price="' +
          (item.price || '') +
          '" data-sku="' +
          (item.sku || '') +
          '">' +
          (item.image_url
            ? '<img class="ticket-product-line__image" src="' + item.image_url + '" alt="">'
            : '<span class="ticket-product-line__image ticket-product-line__image--placeholder"></span>') +
          '<span class="ticket-product-line__meta">' +
          '<span class="ticket-product-line__title">' +
          (item.title || 'Item') +
          '</span>' +
          '<span class="ticket-product-line__qty">× ' +
          (item.quantity || 1) +
          '</span>' +
          '</span>';
        list.appendChild(label);
      });

      const placeholder = pickProductBtn.querySelector('.ticket-pick-product__placeholder');
      if (placeholder) placeholder.hidden = true;
    }

    function hydrateOrderSelect() {
      if (!orderSelect) return;
      orderSelect.innerHTML = '<option value="">Please select order</option>';
      if (!ordersCache.length) {
        orderSelect.innerHTML = '<option value="">No orders found for this email</option>';
        return;
      }
      ordersCache.forEach(function (order) {
        const option = document.createElement('option');
        option.value = String(order.id || order.name || '');
        option.textContent =
          (order.name || order.order_number || 'Order') +
          ' — ' +
          (order.total_price || '') +
          ' ' +
          (order.currency || '');
        orderSelect.appendChild(option);
      });
    }

    async function loadOrders(showErrors) {
      const email = emailInput.value.trim();
      const normalizedEmail = email.toLowerCase();
      if (!validateEmail(email)) {
        if (showErrors) setFieldError(emailInput, 'Please enter a valid email address.');
        return [];
      }

      if (ordersCache.length && normalizedEmail === ordersCacheEmail) {
        hydrateOrderSelect();
        return ordersCache;
      }

      orderSelect.innerHTML = '<option value="">Loading orders...</option>';
      try {
        const payload = await proxyFetch(
          config,
          '/orders?email=' + encodeURIComponent(email),
          { method: 'GET' }
        );
        ordersCache = Array.isArray(payload)
          ? payload
          : Array.isArray(payload && payload.orders)
            ? payload.orders
            : [];
        ordersCacheEmail = normalizedEmail;
        hydrateOrderSelect();
        if (!ordersCache.length) {
          if (orderHint) {
            orderHint.textContent =
              'No orders were found. Please check your email or include your order number in the message.';
          }
        } else if (orderHint) {
          orderHint.textContent = 'Choose the order related to your request.';
        }
        return ordersCache;
      } catch (error) {
        ordersCache = Array.isArray(config.preloadedOrders) ? config.preloadedOrders.slice() : [];
        ordersCacheEmail = ordersCache.length ? (config.customerEmail || '').trim().toLowerCase() : '';
        if (ordersCache.length) {
          hydrateOrderSelect();
          if (showErrors && orderHint) {
            orderHint.textContent = 'Loaded orders from your account. API lookup is temporarily unavailable.';
          }
          return ordersCache;
        }
        orderSelect.innerHTML = '<option value="">Unable to load orders</option>';
        if (showErrors) {
          setFieldError(orderSelect, error.message || 'Unable to load orders.');
        }
        return [];
      }
    }

    async function openProductPicker() {
      setFieldError(productRow, '');
      const email = emailInput.value.trim();
      if (!validateEmail(email)) {
        setFieldError(emailInput, 'Please enter your email before selecting products.');
        emailInput.focus();
        return;
      }

      setSpinning(true);
      try {
        let orders = ordersCache;
        if (!orders.length) {
          orders = await loadOrders(false);
        }
        if (!orders.length) {
          setFieldError(productRow, 'No orders found for this email.');
          return;
        }

        if (!selectedOrder) {
          if (orders.length === 1) {
            selectedOrder = orders[0];
          } else if (orderSelect && orderSelect.value) {
            selectedOrder = orders.find(function (item) {
              return String(item.id || item.name) === orderSelect.value;
            });
          }
        }

        if (!selectedOrder) {
          setFieldError(
            productRow,
            'Please choose an order first, or ensure your email has a recent order.'
          );
          if (!orderRow.hidden) {
            orderSelect.focus();
          }
          return;
        }

        renderProductPanel(selectedOrder, selectedOrder.line_items || []);
      } finally {
        setSpinning(false);
      }
    }

    function collectSelectedProducts() {
      const products = [];
      if (!productPanel) return products;
      productPanel.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
        products.push({
          id: input.value,
          name: input.getAttribute('data-title') || 'Item',
          quantity: Number(input.getAttribute('data-quantity') || 1),
          price: input.getAttribute('data-price') || '',
          sku: input.getAttribute('data-sku') || ''
        });
      });
      return products;
    }

    function addPreview(container, file, onRemove) {
      const item = document.createElement('div');
      item.className = 'ticket-upload-item';
      if (file.type && file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        item.appendChild(img);
      } else {
        item.textContent = file.name;
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ticket-upload-remove';
      remove.textContent = '×';
      remove.addEventListener('click', function () {
        const img = item.querySelector('img');
        if (img) URL.revokeObjectURL(img.src);
        item.remove();
        onRemove(file);
        updateUploadCounters();
      });
      item.appendChild(remove);
      container.appendChild(item);
    }

    async function uploadFile(file) {
      const body = new FormData();
      body.append('file', file);

      const directUrl = config.uploadUrl;
      if (directUrl) {
        const response = await fetch(directUrl, {
          method: 'POST',
          body: body,
          headers: { Accept: 'application/json' }
        });
        const text = await response.text();
        let payload = null;
        try {
          payload = text ? JSON.parse(text) : null;
        } catch (error) {
          payload = { message: text };
        }
        if (!response.ok || !payload || payload.success === false) {
          const message =
            (payload && payload.message) || 'Upload failed (' + response.status + ')';
          throw new Error(message);
        }
        return payload;
      }

      return proxyFetch(config, '/upload', { method: 'POST', body });
    }

    buildQuestionTypeTree();
    categoryInput.value = initial.category || '';
    subcategoryInput.value = initial.subcategory || '';
    if (config.customerEmail && emailInput && !emailInput.value) {
      emailInput.value = config.customerEmail;
    }
    config.customerEmail = emailInput.value.trim();
    applyQuestionType();
    applyFieldRules();
    restoreEmail();
    updateUploadCounters();
    if (ordersCache.length) hydrateOrderSelect();
    setTimeout(function () {
      setSpinning(false);
    }, 200);

    if (emailInput.value) {
      loadOrders(false);
    }

    if (questionTypeTrigger) {
      questionTypeTrigger.setAttribute('aria-expanded', 'false');
      questionTypeTrigger.addEventListener('click', function () {
        if (questionPanel && !questionPanel.hidden) {
          closeQuestionDrawer();
        } else {
          openQuestionDrawer();
        }
      });
    }
    document.addEventListener('click', function (event) {
      if (!questionPanel || questionPanel.hidden) return;
      if (root.contains(event.target)) return;
      closeQuestionDrawer();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeQuestionDrawer();
      }
    });
    if (questionPanel) {
      questionPanel.addEventListener('click', function (event) {
        event.stopPropagation();
      });
    }

    emailInput.addEventListener(
      'input',
      debounce(function () {
        const email = emailInput.value.trim();
        if (validateEmail(email)) {
          localStorage.setItem(EMAIL_STORAGE_KEY, email);
        }
        resetOrderAndProducts();
        if (validateEmail(email)) {
          loadOrders(false);
        }
      }, 400)
    );

    if (orderSelect) {
      orderSelect.addEventListener('focus', function () {
        if (!ordersCache.length && validateEmail(emailInput.value.trim())) {
          loadOrders(false);
        }
      });
      orderSelect.addEventListener('change', function () {
        selectedOrder = ordersCache.find(function (item) {
          return String(item.id || item.name) === orderSelect.value;
        });
        selectedProducts = [];
        if (productPanel) {
          productPanel.hidden = true;
          productPanel.innerHTML = '';
        }
        if (selectedOrder && !productRow.hidden) {
          renderProductPanel(selectedOrder, selectedOrder.line_items || []);
        }
      });
    }

    if (pickProductBtn) {
      pickProductBtn.addEventListener('click', openProductPicker);
    }

    if (imageInput) {
      imageInput.addEventListener('change', function () {
        const files = Array.from(imageInput.files || []);
        let limitHit = false;
        files.forEach(function (file) {
          if (uploadedImages.length >= maxImages) {
            setFieldError(imageInput, 'You can upload up to ' + maxImages + ' images.');
            limitHit = true;
            return;
          }
          if (!file.type || !file.type.startsWith('image/')) {
            setFieldError(imageInput, 'Please select image files only.');
            limitHit = true;
            return;
          }
          if (file.size > maxImageBytes) {
            setFieldError(
              imageInput,
              'Each image must be less than ' + formatMb(maxImageBytes) + 'MB.'
            );
            limitHit = true;
            return;
          }
          uploadedImages.push(file);
          addPreview(imagePreview, file, function (removed) {
            const index = uploadedImages.indexOf(removed);
            if (index >= 0) uploadedImages.splice(index, 1);
          });
        });
        imageInput.value = '';
        updateUploadCounters();
        if (!limitHit) setFieldError(imageInput, '');
      });
    }

    if (videoInput) {
      videoInput.addEventListener('change', function () {
        const file = videoInput.files && videoInput.files[0];
        videoPreview.innerHTML = '';
        uploadedVideo = null;
        if (!file) {
          updateUploadCounters();
          return;
        }
        if (file.size > maxVideoBytes) {
          setFieldError(
            videoInput,
            'The video must be less than ' + formatMb(maxVideoBytes) + 'MB.'
          );
          videoInput.value = '';
          return;
        }
        if (!file.type || !file.type.startsWith('video/')) {
          setFieldError(videoInput, 'Please select a video file.');
          videoInput.value = '';
          return;
        }
        uploadedVideo = file;
        addPreview(videoPreview, file, function () {
          uploadedVideo = null;
          videoInput.value = '';
        });
        updateUploadCounters();
        setFieldError(videoInput, '');
      });
    }

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      errorBanner.hidden = true;

      [emailInput, messageInput, orderSelect, productRow, imageInput, videoInput].forEach(function (
        field
      ) {
        if (field) setFieldError(field, '');
      });

      const email = emailInput.value.trim();
      const message = messageInput.value.trim();
      const category = categoryInput.value;
      const subcategory = subcategoryInput.value.trim();

      if (!category) {
        setFieldError(questionTypeTrigger, 'Question type is required.');
        return;
      }
      if (!validateEmail(email)) {
        setFieldError(emailInput, 'Please enter a valid email address.');
        return;
      }
      if (!message) {
        setFieldError(messageInput, 'Please enter your message.');
        return;
      }

      if (isRequired(rules.requireOrder) && !orderRow.hidden && !orderSelect.value && !selectedOrder) {
        setFieldError(orderSelect, 'Please select an order number.');
        return;
      }

      selectedProducts = collectSelectedProducts();
      if (isRequired(rules.requireProduct) && !productRow.hidden && !selectedProducts.length) {
        setFieldError(productRow, 'Please select at least one product.');
        return;
      }

      if (isRequired(rules.requireImage) && imageRow && !uploadedImages.length) {
        setFieldError(imageInput, 'Please upload at least one image.');
        return;
      }

      submitButton.disabled = true;
      submitButton.textContent = config.submittingLabel || 'Submitting...';

      try {
        const orderFromSelect = ordersCache.find(function (item) {
          return String(item.id || item.name) === orderSelect.value;
        });
        const activeOrder = selectedOrder || orderFromSelect;

        const attachmentPayload = [];
        const uploadFailures = [];
        const allUploads = uploadedVideo
          ? uploadedImages.concat([uploadedVideo])
          : uploadedImages.slice();

        for (const file of allUploads) {
          try {
            const uploaded = await uploadFile(file);
            if (uploaded && uploaded.url) {
              attachmentPayload.push({
                name: file.name,
                url: uploaded.url,
                fileName: uploaded.fileName,
                contentType: uploaded.contentType || file.type
              });
            }
          } catch (error) {
            uploadFailures.push(file.name + ': ' + (error.message || 'upload failed'));
          }
        }

        if (uploadFailures.length) {
          throw new Error('Some files could not be uploaded. ' + uploadFailures.join(' '));
        }

        const payload = await proxyFetch(config, '/ticket', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            name: nameInput.value.trim(),
            category,
            categoryLabel: categoryMap[category] || category,
            subcategory,
            message,
            website: (form.querySelector('[name="website"]') || {}).value || '',
            selectedOrder: activeOrder
              ? {
                  id: activeOrder.id,
                  name: activeOrder.name,
                  order_number: activeOrder.order_number,
                  status: activeOrder.status,
                  total_price: activeOrder.total_price,
                  currency: activeOrder.currency,
                  created_at: activeOrder.created_at,
                  selected_products: selectedProducts
                }
              : undefined,
            uploadedFiles: attachmentPayload
          })
        });

        form.hidden = true;
        successEl.hidden = false;
        const ref = payload && payload.conversation_id ? String(payload.conversation_id) : '';
        const refEl = successEl.querySelector('[data-ticket-reference]');
        if (refEl) {
          refEl.textContent = ref ? 'Reference #' + ref : '';
          refEl.hidden = !ref;
        }
      } catch (error) {
        errorBanner.textContent =
          (error && error.message) || 'Unable to submit your ticket. Please try again.';
        errorBanner.hidden = false;
      } finally {
        submitButton.disabled = false;
        submitButton.textContent = config.submitLabel || 'Submit';
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.contact-ticket-wrap[data-section-id]').forEach(initSection);
  });
})();
